import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Box, Flex } from 'components/Layout/Layout';
import { FormattedMessage, useIntl } from 'react-intl';
import FormattedHtmlMessage from 'components/Intl/FormattedHtmlMessage';
import Media from 'react-media';
import styles from 'theme';
import H2 from 'components/H2/H2';
import messages from './messages';

import { ROUTE_ANALYSE_OVERVIEW } from 'RouteList';
import VideoAnalyseView from './components/VideoAnalyseView';
import AnalyseCaroussel, { Thumb } from './components/AnalyseCaroussel';
import AnalyseDropZone from './components/AnalyseDropZone';
import { useQuery } from '@apollo/client';
import { QUERY_GET_VIDEO_ANALYSES } from '../../services/graphql/video-analyse-query';
import P from '../../components/P/P';
import { VideoAnalyse } from './store/model/index';
import { sort, SORT_DATA_TYPES } from '../../utils/array';
import { useStores } from '../../store/useStores';

function Overview({ videoAnalyseId }) {
  const intl = useIntl();
  const { fileUploadStore, authStore, routing, uiState } = useStores();
  const [email, setEmail] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onDrop = useCallback(
    acceptedFiles => {
      if (acceptedFiles.length > 0) {
        fileUploadStore.uploadVideos(acceptedFiles);
      }
    },
    [fileUploadStore]
  );

  const setVideoAnalyseActive = useCallback(
    videoAnalyse => {
      if (videoAnalyse.video.transcoded) {
        routing.push(`${ROUTE_ANALYSE_OVERVIEW}/${videoAnalyse.id}`);
      } else {
        uiState.createAlert({
          title: intl.formatMessage(messages.analyseOverviewModalProgressTitle),
          message: intl.formatMessage(
            messages.analyseOverviewModalProgressDescription
          ),
          cancel: intl.formatMessage(
            messages.analyseOverviewModalProgressButton
          )
        });
      }
    },
    [intl, routing, uiState]
  );

  const shareVideo = useCallback(
    videoAnalyse => {
      setVideoAnalyseActive(videoAnalyse);
      setTimeout(() => {
        const element = document.getElementById('shareToEmailInput');
        if (element) {
          element.focus();
        }
      }, 100);
    },
    [setVideoAnalyseActive]
  );

  const deleteAnalyse = useCallback(
    async (mutation, analyse) => {
      uiState.createAlert({
        title: intl.formatMessage(
          messages.analyseOverviewModalDeleteAnalyseTitle
        ),
        message: intl.formatMessage(
          messages.analyseOverviewModalDeleteAnalyseDescription
        ),
        ok: intl.formatMessage(
          messages.analyseOverviewModalDeleteAnalyseButtonOK
        ),
        okHandler: async () => {
          await mutation({ variables: { analyseId: analyse.id } });
        },
        cancel: intl.formatMessage(
          messages.analyseOverviewModalDeleteAnalyseButtonCancel
        )
      });
    },
    [intl, uiState]
  );

  const deleteVideoAnalyse = useCallback(
    async (mutation, videoAnalyse) => {
      uiState.createAlert({
        title: intl.formatMessage(
          messages.analyseOverviewModalDeleteVideoTitle
        ),
        message: intl.formatMessage(
          messages.analyseOverviewModalDeleteVideoDescription
        ),
        ok: intl.formatMessage(
          messages.analyseOverviewModalDeleteVideoButtonOK
        ),
        okHandler: async () => {
          await mutation({ variables: { videoAnalyseId: videoAnalyse.id } });
          routing.push(ROUTE_ANALYSE_OVERVIEW);
        },
        cancel: intl.formatMessage(
          messages.analyseOverviewModalDeleteVideoButtonCancel
        )
      });
    },
    [intl, routing, uiState]
  );

  const { canDrop, isImpersonator, user } = {
    canDrop: fileUploadStore.canDrop,
    isImpersonator: authStore.isImpersonator,
    user: authStore.user
  };

  const currentUser = isImpersonator || user;

  const { loading, error, data } = useQuery(QUERY_GET_VIDEO_ANALYSES, {
    variables: { personId: isImpersonator ? isImpersonator.id : user.id },
    fetchPolicy: 'cache-and-network'
  });

  if (loading) return <P>Bezig met laden...</P>;
  if (error) return <P danger>{error.message}</P>;

  const items = sort(
    (data?.getVideoAnalyses || []).map(e => new VideoAnalyse(e)),
    {
      keys: [
        {
          key: 'date_created',
          dataType: SORT_DATA_TYPES.DATE_STRING,
          desc: true
        }
      ]
    }
  );

  const activeVideoAnalyse = videoAnalyseId
    ? items.find(i => i.id === videoAnalyseId)
    : null;

  const hasVideoAnalyses = items.length > 0;

  return (
    <Flex flexWrap="wrap" width={1}>
      <Box width={1} mt={2} mb={4}>
        <H2 upper>
          <FormattedMessage {...messages.analiseOverviewTitle} />
        </H2>
      </Box>

      <FormattedHtmlMessage
        id={messages.analiseIntroText.id}
        defaultMessage={messages.analiseIntroText.defaultMessage}
      />

      <Flex width={1} flexWrap="wrap">
        {!isImpersonator && (
          <Box width={[1, 1 / 3, 1 / 4]} pr={[0, 2, 2]} pb={[3, 0]}>
            <AnalyseDropZone onDrop={onDrop} canDrop={canDrop} />
          </Box>
        )}

        <Flex
          width={[1, 2 / 3, isImpersonator ? 1 : 3 / 4]}
          pl={[0, 2, 2]}
          alignItems={!hasVideoAnalyses ? 'center' : 'stretch'}
        >
          {hasVideoAnalyses &&
            items.length === 1 &&
            items.map(dataItem =>
              dataItem && dataItem.video ? (
                <Box
                  key={dataItem.id}
                  width={1 / 3}
                  style={{ position: 'relative' }}
                >
                  <Thumb
                    isactive={
                      activeVideoAnalyse
                        ? dataItem.id === activeVideoAnalyse.id
                        : false
                    }
                    videoAnalyse={dataItem}
                    onShare={shareVideo}
                    sharedBy={dataItem.sharedFrom}
                    sharedToMe={
                      dataItem.sharedFrom &&
                      dataItem.sharedFrom.id !== currentUser.id
                    }
                    shareable={
                      dataItem.sharedFrom &&
                      dataItem.sharedFrom.id === currentUser.id
                    }
                    video={dataItem.video}
                    onClick={() => setVideoAnalyseActive(dataItem)}
                  />
                </Box>
              ) : null
            )}

          {hasVideoAnalyses && items.length > 1 && (
            <Media query={{ maxWidth: styles.mq.large }}>
              {matches => (
                <AnalyseCaroussel
                  items={items}
                  activeId={activeVideoAnalyse && activeVideoAnalyse.id}
                  user={currentUser}
                  onClick={setVideoAnalyseActive}
                  onShare={shareVideo}
                  slidesToShow={
                    matches ? (isImpersonator ? 3 : 2) : isImpersonator ? 4 : 3
                  }
                />
              )}
            </Media>
          )}

          {!hasVideoAnalyses && (
            <FormattedMessage {...messages.analiseNoAnalyses} />
          )}
        </Flex>
      </Flex>

      {activeVideoAnalyse && (
        <VideoAnalyseView
          videoAnalyse={activeVideoAnalyse}
          deleteVideoAnalyse={deleteVideoAnalyse}
          deleteAnalyse={deleteAnalyse}
          showControls={!isImpersonator}
          onUpdateEmail={e => setEmail(e.target.value)}
          email={email}
        />
      )}
    </Flex>
  );
}

export default observer(Overview);
